import { 
  collection, doc, onSnapshot, query, where, getDocs, updateDoc, deleteDoc, getDoc, setDoc, orderBy, limit, deleteField
} from "firebase/firestore";
import { db } from "../firebase";
import { 
    RiderProfile, RiderLocation, RiderStatus, CallSession, DeliveryRequest
} from "../types";
import { getTenantId } from "./tenantService";

export const registerRider = async (rider: RiderProfile) => {
    await setDoc(doc(db, "riders", rider.id), rider);
};

export const updateRiderLocation = async (loc: RiderLocation | null) => {
    if (!loc) return;
    const riderId = localStorage.getItem('app_rider_id'); // Using rider scope
    if (riderId) {
        await setDoc(doc(db, "rider_locations", riderId), { ...loc, timestamp: Date.now() }, { merge: true });
    }
};

export const subscribeToRiderLocation = (callback: (loc: RiderLocation | null) => void) => {
    const riderId = localStorage.getItem('app_rider_id');
    if (!riderId) {
        callback(null);
        return () => {};
    }
    return onSnapshot(doc(db, "riders", riderId), (docSnap) => {
        if (docSnap.exists()) {
            const data = docSnap.data();
            if (data?.locationLat && data?.locationLng) {
                callback({
                    lat: data.locationLat,
                    lng: data.locationLng,
                    timestamp: data.location?.timestamp || data.timestamp || Date.now()
                });
            } else if (data?.location?.lat && data?.location?.lng) {
                callback({
                    lat: data.location.lat,
                    lng: data.location.lng,
                    timestamp: data.location.timestamp || data.timestamp || Date.now()
                });
            } else {
                // Try reading rider_locations as fallback
                getDoc(doc(db, "rider_locations", riderId)).then((locSnap) => {
                    if (locSnap.exists()) {
                        callback(locSnap.data() as RiderLocation);
                    } else {
                        callback(null);
                    }
                }).catch(() => callback(null));
            }
        } else {
            getDoc(doc(db, "rider_locations", riderId)).then((locSnap) => {
                if (locSnap.exists()) {
                    callback(locSnap.data() as RiderLocation);
                } else {
                    callback(null);
                }
            }).catch(() => callback(null));
        }
    });
};

export const subscribeToRiderLocationById = (
    riderId: string, 
    callback: (loc: RiderLocation | null) => void,
    tenantId?: string
) => {
    return onSnapshot(doc(db, "riders", riderId), (docSnap) => {
        if (docSnap.exists()) {
            const data = docSnap.data();
            if (data?.locationLat && data?.locationLng) {
                callback({
                    lat: data.locationLat,
                    lng: data.locationLng,
                    timestamp: data.location?.timestamp || data.timestamp || Date.now()
                });
                return;
            } else if (data?.location?.lat && data?.location?.lng) {
                callback({
                    lat: data.location.lat,
                    lng: data.location.lng,
                    timestamp: data.location.timestamp || data.timestamp || Date.now()
                });
                return;
            }
        }
        
        // Fallback: Check rider_locations or tenant doc
        getDoc(doc(db, "rider_locations", riderId)).then((locSnap) => {
            if (locSnap.exists()) {
                callback(locSnap.data() as RiderLocation);
            } else if (tenantId && tenantId !== 'root') {
                getDoc(doc(db, `tenants/${tenantId}/riders`, riderId)).then((tSnap) => {
                    if (tSnap.exists()) {
                        const tData = tSnap.data();
                        if (tData?.locationLat && tData?.locationLng) {
                            callback({ lat: tData.locationLat, lng: tData.locationLng, timestamp: tData.timestamp || Date.now() });
                        } else if (tData?.location?.lat && tData?.location?.lng) {
                            callback({ lat: tData.location.lat, lng: tData.location.lng, timestamp: tData.location.timestamp || Date.now() });
                        }
                    } else {
                        callback(null);
                    }
                }).catch(() => callback(null));
            } else {
                callback(null);
            }
        }).catch(() => callback(null));
    });
};

export const updateRiderStatus = async (
    riderId: string, 
    status: RiderStatus, 
    location?: { lat: number; lng: number; accuracy?: number; heading?: number; speed?: number }, 
    isApproved?: boolean,
    tenantId?: string
) => {
    const updateData: any = { status, timestamp: Date.now() };
    if (location) {
        updateData.locationLat = location.lat;
        updateData.locationLng = location.lng;
        updateData.location = {
            lat: location.lat,
            lng: location.lng,
            timestamp: Date.now(),
            accuracy: location.accuracy !== undefined ? location.accuracy : null,
            heading: location.heading !== undefined ? location.heading : null,
            speed: location.speed !== undefined ? location.speed : null
        };
    }
    if (isApproved === true || isApproved === false) updateData.isApproved = isApproved;
    
    // 1. Update main riders collection
    try {
        await updateDoc(doc(db, "riders", riderId), updateData);
    } catch (e) {
        try { await setDoc(doc(db, "riders", riderId), updateData, { merge: true }); } catch (err) {}
    }

    // 2. Update rider_locations collection for quick fallback access
    if (location) {
        try {
            await setDoc(doc(db, "rider_locations", riderId), {
                lat: location.lat,
                lng: location.lng,
                timestamp: Date.now()
            }, { merge: true });
        } catch (e) {}
    }

    // 3. Update tenant riders subcollection if tenantId provided
    if (tenantId && tenantId !== 'root') {
        try {
            await updateDoc(doc(db, `tenants/${tenantId}/riders`, riderId), updateData);
        } catch (e) {}
    }

    // 4. CRITICAL: Sync live location to active delivery requests & live_orders so TrackingView updates in real time
    if (location) {
        try {
            const locObj = { lat: location.lat, lng: location.lng, lastUpdate: Date.now() };
            
            // Query active delivery requests for this rider
            const q = query(
                collection(db, "delivery_requests"),
                where("riderId", "==", riderId),
                where("status", "in", ["accepted", "picked_up"])
            );
            const reqSnap = await getDocs(q);
            reqSnap.forEach(async (d) => {
                const rData = d.data();
                const orderId = rData.orderId;
                const reqTenant = rData.tenantId || tenantId || 'root';
                if (orderId) {
                    const orderCol = (reqTenant === 'root' || reqTenant === 'unknown') ? 'live_orders' : `tenants/${reqTenant}/live_orders`;
                    try {
                        await updateDoc(doc(db, orderCol, orderId), {
                            internalRiderLocation: locObj,
                            riderLocation: locObj,
                            riderId: riderId
                        });
                    } catch (err) {}
                }
            });
        } catch (e) {
            console.error("Failed to sync location to active orders:", e);
        }
    }
};

export const updateRiderProfile = async (riderId: string, data: Partial<RiderProfile>) => {
    await updateDoc(doc(db, "riders", riderId), data);
};

export const updateRiderVerification = async (riderId: string, isVerified: boolean) => {
    await updateDoc(doc(db, "riders", riderId), { isVerified, status: isVerified ? 'offline' : 'pending_documents' });
};

export const rejectRiderDocument = async (riderId: string) => {
    await updateDoc(doc(db, "riders", riderId), { 
        status: 'pending_documents', 
        isVerified: false 
    });
};

export const sendRiderMessage = async (riderId: string, text: string, sender: 'admin' | 'rider', senderName?: string, tenantId?: string) => {
    const msg = {
        id: Date.now().toString() + Math.random().toString(36).substring(7),
        riderId,
        text,
        sender,
        senderName: senderName || (sender === 'admin' ? 'Admin' : 'Rider'),
        timestamp: Date.now(),
        read: false,
        tenantId: tenantId || null
    };
    await setDoc(doc(db, "rider_messages", msg.id), msg);
};

export const markRiderMessagesAsRead = async (riderId: string, tenantId: string | null) => {
    const currentTenant = tenantId || getTenantId() || 'root';
    const qRider = query(collection(db, "rider_messages"), where("riderId", "==", riderId));
    const snaps = await getDocs(qRider);
    
    // Only mark as read if admin reads it and it's from rider, OR rider reads it and it's from admin
    snaps.forEach(docSnap => {
        const d = docSnap.data();
        if (d.sender === 'admin' && !d.read) {
            updateDoc(docSnap.ref, { read: true });
        }
    });
};

export const subscribeToRiderMessages = (riderId: string, callback: (msgs: any[]) => void) => {
    const qR = query(
        collection(db, "rider_messages"), 
        where("riderId", "==", riderId)
    );
    return onSnapshot(qR, (snapshot) => {
        const list: any[] = [];
        snapshot.forEach((d) => list.push(d.data()));
        list.sort((a,b) => a.timestamp - b.timestamp);
        callback(list);
    });
};

export const initiateRiderCall = async (riderId: string, adminName: string) => {
    const sessionId = `call_${riderId}_${Date.now()}`;
    const callDoc = doc(db, 'calls', riderId);
    await setDoc(callDoc, {
        sessionId,
        status: 'calling',
        callerName: adminName,
        timestamp: Date.now()
    });
};

export const endRiderCall = async (riderId: string) => {
    await deleteDoc(doc(db, 'calls', riderId));
};

export const subscribeToRider = (riderId: string, callback: (rider: RiderProfile | null) => void) => {
    return onSnapshot(
        doc(db, "riders", riderId), 
        (docSnap) => {
            if (docSnap.exists()) {
                callback({ ...(docSnap.data() as any), id: docSnap.id } as RiderProfile);
            } else {
                callback(null);
            }
        },
        (error) => {
            console.error("[subscribeToRider error]", error);
            callback(null);
        }
    );
};

export const deleteRider = async (riderId: string) => {
    await deleteDoc(doc(db, "riders", riderId));
    // Clean deliveries ideally
};

export const flagRider = async (riderId: string, isFlagged: boolean, reason?: string, flaggedBy?: string, flaggedById?: string) => {
    const riderRef = doc(db, "riders", riderId);
    await updateDoc(riderRef, {
        isFlagged,
        flagReason: reason || null,
        flaggedBy: flaggedBy || null,
        flaggedById: flaggedById || null,
        flaggedAt: isFlagged ? Date.now() : null
    });
};

export const addRiderEarnings = async (riderId: string, amount: number, collectedAmount: number = 0, tenantName?: string) => {
    try {
        const riderRef = doc(db, "riders", riderId);
        const riderDoc = await getDoc(riderRef);
        if (riderDoc.exists()) {
            const data: any = riderDoc.data();
            const updates: any = {
                currentEarnings: (data.currentEarnings || 0) + amount,
                lifetimeEarnings: (data.lifetimeEarnings || 0) + amount,
                cashCollected: (data.cashCollected || 0) + collectedAmount,
                totalDeliveries: (data.totalDeliveries || 0) + 1,
                // Also update the fields used by the UI:
                totalEarnings: (data.totalEarnings || 0) + amount,
                totalCollected: (data.totalCollected || 0) + collectedAmount,
                currentSessionEarnings: (data.currentSessionEarnings || 0) + amount,
                currentSessionDeliveries: (data.currentSessionDeliveries || 0) + 1,
                currentSessionCollected: (data.currentSessionCollected || 0) + collectedAmount,
            };
            
            if (collectedAmount > 0 && tenantName) {
                const updatedCredits = { ...(data.creditsToTenants || {}) };
                updatedCredits[tenantName] = (updatedCredits[tenantName] || 0) + collectedAmount;
                updates.creditsToTenants = updatedCredits;
                
                const updatedCollectedByTenant = { ...(data.collectedByTenant || {}) };
                updatedCollectedByTenant[tenantName] = (updatedCollectedByTenant[tenantName] || 0) + collectedAmount;
                updates.collectedByTenant = updatedCollectedByTenant;
            }
            
            await updateDoc(riderRef, updates);
        }
    } catch(e) {}
};

export const subtractRiderEarnings = async (riderId: string, amount: number, collectedAmount: number = 0, tenantName?: string) => {
    try {
        const riderRef = doc(db, "riders", riderId);
        const riderDoc = await getDoc(riderRef);
        if (riderDoc.exists()) {
            const data: any = riderDoc.data();
            const updates: any = {
                currentEarnings: (data.currentEarnings || 0) - amount,
                lifetimeEarnings: (data.lifetimeEarnings || 0) - amount,
                cashCollected: (data.cashCollected || 0) - collectedAmount,
                totalDeliveries: Math.max(0, (data.totalDeliveries || 0) - 1),
                // Also update the fields used by the UI:
                totalEarnings: (data.totalEarnings || 0) - amount,
                totalCollected: (data.totalCollected || 0) - collectedAmount,
                currentSessionEarnings: (data.currentSessionEarnings || 0) - amount,
                currentSessionDeliveries: Math.max(0, (data.currentSessionDeliveries || 0) - 1),
                currentSessionCollected: (data.currentSessionCollected || 0) - collectedAmount,
            };
            
            if (collectedAmount > 0 && tenantName) {
                const updatedCredits = { ...(data.creditsToTenants || {}) };
                updatedCredits[tenantName] = (updatedCredits[tenantName] || 0) - collectedAmount;
                updates.creditsToTenants = updatedCredits;
                
                const updatedCollectedByTenant = { ...(data.collectedByTenant || {}) };
                updatedCollectedByTenant[tenantName] = (updatedCollectedByTenant[tenantName] || 0) - collectedAmount;
                updates.collectedByTenant = updatedCollectedByTenant;
            }
            
            await updateDoc(riderRef, updates);
        }
    } catch(e) {}
};

export const payRiderEarnings = async (riderId: string, amountToPay: number) => {
    const riderRef = doc(db, "riders", riderId);
    const docSnap = await getDoc(riderRef);
    if (docSnap.exists()) {
        const data: any = docSnap.data();
        const currentTotal = data.totalEarnings ?? data.currentEarnings ?? 0;
        const remainingTotal = Math.max(0, currentTotal - amountToPay);
        const remainingCurrent = Math.max(0, (data.currentEarnings || 0) - amountToPay);
        const remainingSession = Math.max(0, (data.currentSessionEarnings || 0) - amountToPay);
        
        await updateDoc(riderRef, {
            totalEarnings: remainingTotal,
            currentEarnings: remainingCurrent,
            currentSessionEarnings: remainingSession,
            lastPaidAt: Date.now(),
            lastPaidAmount: amountToPay
        });
    }
};

export const closeRiderWorkday = async (riderId: string, deliveries: number, earnings: number, collectedCash: number) => {
    const shift = {
        id: `shift_${Date.now()}`,
        riderId,
        date: Date.now(),
        deliveries,
        earnings,
        collectedCash
    };
    await setDoc(doc(db, "rider_shifts", shift.id), shift);
    
    // reset current daily numbers? The updated structure reset cashCollected manually
    const riderRef = doc(db, "riders", riderId);
    await updateDoc(riderRef, {
        cashCollected: 0,
        creditsToTenants: {}
    });
};

export const subscribeToRiderWorkdays = (riderId: string, callback: (workdays: any[]) => void) => {
    const qWorkday = query(collection(db, "rider_shifts"), where("riderId", "==", riderId));
    return onSnapshot(qWorkday, (snapshot) => {
        const list: any[] = [];
        snapshot.forEach(doc => list.push(doc.data()));
        list.sort((a,b) => b.date - a.date);
        callback(list);
    });
};

export const saveRiderShift = async (shift: any) => {
    await setDoc(doc(db, "rider_shifts", shift.id), shift);
};

export const closeRiderShift = async (riderId: string, stats: { totalDeliveries: number; totalEarnings: number; totalCollected: number; collectedByTenant?: Record<string, number> }) => {
    const shift = {
        id: `shift_${Date.now()}`,
        riderId,
        date: Date.now(),
        endTime: Date.now(),
        ...stats
    };
    await setDoc(doc(db, "rider_shifts", shift.id), shift);
    
    const riderRef = doc(db, "riders", riderId);
    await updateDoc(riderRef, {
        cashCollected: 0,
        creditsToTenants: {},
        totalEarnings: 0,
        totalDeliveries: 0,
        totalCollected: 0,
        collectedByTenant: {},
        currentEarnings: 0,
        currentSessionEarnings: 0,
        currentSessionDeliveries: 0,
        currentSessionCollected: 0
    });
};

export const getRiderShifts = async (riderId: string): Promise<any[]> => {
    const qWorkday = query(collection(db, "rider_shifts"), where("riderId", "==", riderId));
    const res = await getDocs(qWorkday);
    return res.docs.map(d => d.data()).sort((a,b) => b.date - a.date);
};

export const subscribeToRiders = (callback: (riders: RiderProfile[]) => void) => {
    return onSnapshot(collection(db, "riders"), (snapshot) => {
        const riders: RiderProfile[] = [];
        snapshot.forEach((doc) => {
            const data = doc.data() as any;
            const rider: RiderProfile = { ...data, id: doc.id };
            if (!rider.location && data.locationLat && data.locationLng) {
                rider.location = {
                    lat: data.locationLat,
                    lng: data.locationLng,
                    timestamp: data.timestamp || Date.now()
                };
            }
            riders.push(rider);
        });
        callback(riders);
    });
};

export const submitRiderFeedback = async (riderId: string, riderName: string | undefined, feedback: string) => {
    const fId = `feedback_${Date.now()}`;
    await setDoc(doc(db, "rider_feedbacks", fId), {
        id: fId,
        riderId,
        riderName: riderName || 'Sconosciuto',
        feedback,
        timestamp: Date.now()
    });
};

export const deleteRiderFeedback = async (feedbackId: string) => {
    await deleteDoc(doc(db, "rider_feedbacks", feedbackId));
};

export const subscribeToRiderFeedbacks = (callback: (feedbacks: any[]) => void) => {
    return onSnapshot(collection(db, "rider_feedbacks"), (snapshot) => {
        const list: any[] = [];
        snapshot.forEach(d => list.push(d.data()));
        list.sort((a,b) => b.timestamp - a.timestamp);
        callback(list);
    });
};

export const subscribeToOnlineRiders = (callback: (riders: RiderProfile[]) => void) => {
    const qRiders = query(
        collection(db, "riders"),
        where("isVerified", "==", true),
        where("status", "in", ["online", "busy"])
    );
    
    return onSnapshot(qRiders, (snapshot) => {
        const list: RiderProfile[] = [];
        snapshot.forEach(d => {
            const data = d.data() as any;
            const rider: RiderProfile = { ...data, id: d.id };
            if (!rider.location && data.locationLat && data.locationLng) {
                rider.location = {
                    lat: data.locationLat,
                    lng: data.locationLng,
                    timestamp: data.timestamp || Date.now()
                };
            }
            list.push(rider);
        });
        callback(list);
    });
};

export const subscribeToIncomingCalls = (riderId: string, onCall: (session: CallSession) => void) => {
    if (!riderId) return () => {};
    return onSnapshot(doc(db, 'calls', riderId), (docSnap) => {
        if (docSnap.exists()) {
            const data: any = docSnap.data();
            if (data.status === 'calling') {
                onCall(data as CallSession);
            }
        }
    });
};

export const findNearbyRiders = async (lat: number, lng: number, radiusKm: number = 5): Promise<RiderProfile[]> => {
    const qOnline = query(
        collection(db, "riders"),
        where("status", "in", ["online", "busy"])
    );
    const snapshot = await getDocs(qOnline);
    const riders: RiderProfile[] = [];
    
    snapshot.forEach(s => {
        const r: any = { ...(s.data() as any), id: s.id };
        if (r.locationLat && r.locationLng) {
            const dist = Math.sqrt(Math.pow(r.locationLat - lat, 2) + Math.pow(r.locationLng - lng, 2));
            // Crude approx: 0.01 deg = ~1km
            if (dist < (radiusKm * 0.01)) {
                 riders.push(r as RiderProfile);
            }
        }
    });
    
    return riders;
};

export const loginRider = async (email: string, pass: string): Promise<RiderProfile | null> => {
    const qUser = query(collection(db, "riders"), where("email", "==", email));
    const users = await getDocs(qUser);
    
    if (!users.empty) {
        const u = users.docs[0];
        const data = u.data() as RiderProfile;
        if (data.password === pass) {
            return { ...data, id: u.id };
        }
    }
    return null;
};

export const createDeliveryRequest = async (request: DeliveryRequest) => {
    await setDoc(doc(db, "delivery_requests", request.id), request);
};

export const subscribeToDeliveryRequests = (id: string, callback: (requests: DeliveryRequest[]) => void, type: 'rider' | 'shop' = 'rider') => {
    let qBase;
    
    if (type === 'rider') {
        qBase = query(
            collection(db, "delivery_requests"), 
            where("status", "in", ["pending", "counter_offer", "accepted", "picked_up"])
        );
    } else {
        qBase = query(
            collection(db, "delivery_requests"), 
            where("status", "in", ["pending", "counter_offer", "accepted", "picked_up"]),
            where("tenantId", "==", id)
        );
    }

    return onSnapshot(qBase, (snapshot) => {
        const list: DeliveryRequest[] = [];
        snapshot.forEach(d => list.push({ ...(d.data() as any), id: d.id } as DeliveryRequest));
        
        if (type === 'rider') {
            const now = Date.now();
            const valid = list.filter(r => {
                // Must be relevant to this rider specifically, OR pending
                const isRelevantToMe = r.riderId === id || r.counterOfferRiderId === id || r.status === 'pending';
                if (!isRelevantToMe) return false;
                
                // If pending/counter_offer, expire after 30 minutes (1800000ms) to allow reload and delay
                if (r.status === 'pending' || r.status === 'counter_offer') {
                    return (now - r.timestamp < 1800000);
                }
                return true;
            });
            callback(valid);
        } else {
            callback(list);
        }
    });
};

export const acceptDeliveryRequest = async (requestId: string, riderId: string) => {
    await updateDoc(doc(db, "delivery_requests", requestId), {
        status: 'accepted',
        acceptedAt: Date.now(),
        riderId: riderId
    });

    // Sync riderId to the actual order in live_orders or /tenants/:tenantId/live_orders
    try {
        const reqDoc = await getDoc(doc(db, "delivery_requests", requestId));
        if (reqDoc.exists()) {
            const reqData = reqDoc.data();
            const orderId = reqData.orderId;
            let tenantId = reqData.tenantId || 'root';
            if (tenantId === 'unknown') tenantId = 'root';
            
            if (orderId) {
                const ordersCol = tenantId === 'root' ? 'live_orders' : `tenants/${tenantId}/live_orders`;
                await updateDoc(doc(db, ordersCol, orderId), { 
                    riderId: riderId
                });
            }
        }
    } catch (e) {
        console.error("Failed to sync riderId to order document:", e);
    }
};

export const cancelDeliveryAssignment = async (requestId: string) => {
    await updateDoc(doc(db, "delivery_requests", requestId), {
        status: 'cancelled',
        cancelledAt: Date.now()
    });
};

export const deleteDeliveryRequest = async (requestId: string) => {
    await deleteDoc(doc(db, "delivery_requests", requestId));
};

export const subscribeToRiderHistory = (riderId: string, limitCount: number, callback: (requests: DeliveryRequest[]) => void) => {
    const qRider = query(
        collection(db, "delivery_requests"), 
        where("riderId", "==", riderId),
        where("status", "in", ["delivered", "cancelled"]),
        orderBy("timestamp", "desc"),
        limit(limitCount)
    );
    
    return onSnapshot(qRider, (snapshot) => {
        const list: DeliveryRequest[] = [];
        snapshot.forEach(d => list.push({ ...(d.data() as any), id: d.id } as DeliveryRequest));
        callback(list);
    });
};

export const revertDeliveryRequestStatus = async (requestId: string) => {
    const reqRef = doc(db, "delivery_requests", requestId);
    const reqDoc = await getDoc(reqRef);
    if (!reqDoc.exists()) return;
    const reqData = reqDoc.data() as DeliveryRequest;

    await updateDoc(reqRef, {
        status: 'picked_up',
        deliveredAt: deleteField()
    });

    const tenantId = reqData.tenantId || 'root';
    const orderId = reqData.orderId;
    if (orderId) {
        const ordersCol = tenantId === 'root' ? 'live_orders' : `tenants/${tenantId}/live_orders`;
        // Revert live_order status to 'shipped' (which means in transit to customer)
        try {
            await updateDoc(doc(db, ordersCol, orderId), { 
                status: 'shipped',
                completedAt: deleteField()
            });
        } catch (e) {
            console.error("Failed to revert order status", e);
        }
    }
};

export const updateDeliveryRequestStatus = async (requestId: string, status: 'accepted' | 'picked_up' | 'delivered' | 'cancelled' | 'counter_offer' | 'pending', extraData?: any) => {
    const reqRef = doc(db, "delivery_requests", requestId);
    const reqDoc = await getDoc(reqRef);
    if (!reqDoc.exists()) return;
    const reqData = reqDoc.data();

    const updates: any = { status };
    if (status === 'picked_up') updates.pickedUpAt = Date.now();
    if (status === 'delivered') updates.deliveredAt = Date.now();
    if (status === 'cancelled') updates.cancelledAt = Date.now();
    if (status === 'accepted') updates.acceptedAt = Date.now();
    if (status === 'counter_offer') updates.counterOfferAt = Date.now();
    
    if (extraData) {
        Object.assign(updates, extraData);
    }
    
    await updateDoc(reqRef, updates);

    // Auto-update the order status if delivered
    if (status === 'delivered') {
        let tenantId = reqData.tenantId || 'root';
        if (tenantId === 'unknown') tenantId = 'root';
        const orderId = reqData.orderId;
        if (orderId) {
            const ordersCol = tenantId === 'root' ? 'live_orders' : `tenants/${tenantId}/live_orders`;
            try {
                await updateDoc(doc(db, ordersCol, orderId), { 
                    status: 'completed',
                    completedAt: Date.now()
                });
            } catch (e) {
                console.error("Failed to mark order as completed from delivery request", e);
            }
        }
    }
    
    // Also auto-update if picked up (mark as shipped)
    if (status === 'picked_up') {
        let tenantId = reqData.tenantId || 'root';
        if (tenantId === 'unknown') tenantId = 'root';
        const orderId = reqData.orderId;
        if (orderId) {
            const ordersCol = tenantId === 'root' ? 'live_orders' : `tenants/${tenantId}/live_orders`;
            try {
                await updateDoc(doc(db, ordersCol, orderId), { status: 'shipped' });
            } catch (e) {
                console.error("Failed to mark order as shipped from delivery request", e);
            }
        }
    }
};
